import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SEOHead, { BreadcrumbSchema } from '@/components/feature/SEOHead';
import { categories, movies } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSubCategory, setActiveSubCategory] = useState<string | null>(null);

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <SEOHead title="分类未找到 - 星辰影院" />
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-3">分类未找到</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryMovies = movies.filter((m) => m.categorySlug === slug);
  const allSubCategories = [...new Set(categoryMovies.map((m) => m.quality))];

  const filteredMovies = activeSubCategory
    ? categoryMovies.filter((m) => m.quality === activeSubCategory)
    : categoryMovies;

  const breadcrumbItems = [
    { name: '首页', url: '/' },
    { name: category.name, url: `/category/${slug}` },
  ];

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${category.name}有哪些经典电影推荐？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `星辰影院${category.name}频道收录了超过${category.count}部作品，涵盖不同类型和年代的经典影片，所有电影均支持高清在线观看。`,
        },
      },
      {
        '@type': 'Question',
        name: `${category.name}支持什么画质播放？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '星辰影院提供从720P到蓝光4K多种画质选择，用户可根据网络状况自由切换，确保流畅观影体验。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 星辰影院${category.name}大全 | 高清${category.name}免费在线观看`}
        description={`星辰影院${category.name}频道提供海量高清${category.name}资源，最新最全${category.name}免费在线观看，蓝光4K画质不卡顿不缓冲，每日更新。`}
        keywords={`星辰影院,${category.name},高清${category.name},${category.name}在线观看,免费${category.name}`}
        canonicalUrl={`https://www.xingchencinema.com/category/${slug}`}
        ogTitle={`${category.name} - 星辰影院 | 高清免费在线观看`}
      />

      <BreadcrumbSchema items={breadcrumbItems} />

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-6 md:py-8" aria-label="面包屑导航">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1 text-sm text-foreground-500" aria-label="Breadcrumb">
              {breadcrumbItems.map((item, idx) => (
                <span key={idx} className="flex items-center gap-1">
                  {idx > 0 && (
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </span>
                  )}
                  {idx < breadcrumbItems.length - 1 ? (
                    <Link to={item.url} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                      {item.name}
                    </Link>
                  ) : (
                    <span className="text-foreground-800 font-medium">{item.name}</span>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50" aria-label={`${category.name}电影列表`}>
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-600">
                共 {category.count} 部{category.name} · 蓝光4K画质 · 极速播放不缓冲
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6 pb-1">
              <button
                onClick={() => setActiveSubCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSubCategory === null
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200'
                }`}
              >
                全部
              </button>
              {allSubCategories.map((quality) => (
                <button
                  key={quality}
                  onClick={() => setActiveSubCategory(quality)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSubCategory === quality
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200'
                  }`}
                >
                  {quality}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {filteredMovies.map((movie) => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  title={movie.title}
                  titleEn={movie.titleEn}
                  category={movie.category}
                  categorySlug={movie.categorySlug}
                  year={movie.year}
                  rating={movie.rating}
                  quality={movie.quality}
                  duration={movie.duration}
                  imageUrl={movie.imagePortrait}
                />
              ))}
            </div>

            {filteredMovies.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">该分类暂无电影资源</p>
                <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm mt-2 inline-block">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100" aria-label="其他分类">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
              其他电影分类
            </h2>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-200 bg-background-50 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-500 transition-all text-sm text-foreground-700 font-medium whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'CollectionPage',
              name: `${category.name} - 星辰影院`,
              description: `星辰影院${category.name}频道，提供海量高清${category.name}资源。`,
              url: `https://www.xingchencinema.com/category/${slug}`,
              isPartOf: {
                '@type': 'WebSite',
                name: '星辰影院',
                url: 'https://www.xingchencinema.com/',
              },
            },
            faqSchema,
          ],
        }) }}
      />

      <Footer />
    </>
  );
}